import { useEffect, useMemo, useState } from "react";
import {
  Banner,
  Button,
  Checkbox,
  Dialog,
  Field,
  Icon,
  IconButton,
  OptionChips,
  Select,
  TextArea,
  TextInput,
  useCkToast,
} from "@NP-Webber/crm-kit/dist/next/index.js";
import {
  nextApi,
  type AutomationActionDto,
  type AutomationCatalogDto,
  type AutomationConditionDto,
  type AutomationDto,
  type AutomationInput,
  type AutomationRecipientsDto,
  type AutomationTriggerDto,
  type CatalogField,
} from "../../api/nextApi";
import { notifyAutomationsChanged } from "../events";

/**
 * AutomationEditor — "מתי · באילו תנאים · מה לעשות" in one dialog.
 * Opens for a new automation, a template being customized, or an existing automation.
 */
export type AutomationDraft = Partial<AutomationInput> & { id?: string };

const WEEKDAYS = ["ראשון", "שני", "שלישי", "רביעי", "חמישי", "שישי", "שבת"].map((l, i) => ({ value: String(i), label: `יום ${l}` }));
const EVERY = [
  { value: "day", label: "כל יום" },
  { value: "week", label: "כל שבוע" },
  { value: "month", label: "כל חודש" },
  { value: "once", label: "פעם אחת" },
];
const PRIORITY = [
  { value: "low", label: "נמוכה" },
  { value: "normal", label: "רגילה" },
  { value: "high", label: "גבוהה" },
  { value: "urgent", label: "דחופה" },
];
const NO_VALUE_OPS = new Set(["empty", "not_empty"]);

const DEFAULT_SCHEDULE: AutomationTriggerDto = { type: "schedule", every: "day", time: "08:00", audience: "none", oncePerRecord: false };

function defaultAction(type: AutomationActionDto["type"]): AutomationActionDto {
  if (type === "create_task") return { type, title: "", assignTo: { mode: "unassigned" }, dueInDays: 1, priority: "normal" };
  if (type === "send_report") return { type, report: "weekly_summary", to: { mode: "actor" } };
  return { type: "send_email", to: { mode: "actor" }, subject: "", body: "" };
}

export function entityOf(catalog: AutomationCatalogDto | null, trigger: AutomationTriggerDto): string {
  if (!catalog) return "none";
  if (trigger.type === "event") return catalog.events.find((e) => e.key === trigger.event)?.entity ?? "none";
  return catalog.audiences.find((a) => a.key === trigger.audience)?.entity ?? "none";
}

export function AutomationEditor({
  open,
  draft,
  catalog,
  onClose,
  onSaved,
}: {
  open: boolean;
  draft: AutomationDraft | null;
  catalog: AutomationCatalogDto | null;
  onClose: () => void;
  onSaved?: (a: AutomationDto) => void;
}) {
  const toast = useCkToast();
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [screenKey, setScreenKey] = useState("general");
  const [trigger, setTrigger] = useState<AutomationTriggerDto>(DEFAULT_SCHEDULE);
  const [conditions, setConditions] = useState<AutomationConditionDto[]>([]);
  const [actions, setActions] = useState<AutomationActionDto[]>([defaultAction("send_email")]);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open || !draft) return;
    setName(draft.name ?? "");
    setDescription(draft.description ?? "");
    setScreenKey(draft.screenKey ?? "general");
    setTrigger(draft.trigger ?? DEFAULT_SCHEDULE);
    setConditions(draft.conditions ?? []);
    setActions(draft.actions?.length ? draft.actions : [defaultAction("send_email")]);
    setError(null);
  }, [open, draft]);

  const entity = entityOf(catalog, trigger);
  const fields: CatalogField[] = useMemo(() => catalog?.fields[entity] ?? [], [catalog, entity]);
  const tags = useMemo(() => [...fields, ...(catalog?.commonTags ?? [])], [fields, catalog]);
  const staff = catalog?.staff ?? [];

  const recipientModes = useMemo(() => {
    const out: Array<{ value: AutomationRecipientsDto["mode"]; label: string }> = [];
    if (["customer", "payment", "subscription", "income", "form_submission", "payment_request", "task"].includes(entity))
      out.push({ value: "record", label: entity === "form_submission" ? "הממלא (מייל מהטופס)" : "הלקוח" });
    if (entity === "task") out.push({ value: "assignee", label: "האחראי על המשימה" });
    out.push({ value: "actor", label: "אליי" }, { value: "users", label: "אנשי צוות" }, { value: "emails", label: "כתובות מייל" });
    return out;
  }, [entity]);

  const setSchedule = (patch: Partial<Extract<AutomationTriggerDto, { type: "schedule" }>>) =>
    setTrigger((t) => (t.type === "schedule" ? { ...t, ...patch } : t));

  const save = async () => {
    setError(null);
    if (!name.trim()) {
      setError("צריך לתת שם לאוטומציה");
      return;
    }
    const input: AutomationInput = {
      name: name.trim(),
      description: description.trim() || null,
      screenKey,
      templateKey: draft?.templateKey ?? null,
      status: "active",
      trigger,
      conditions: conditions.filter((c) => c.field),
      actions,
    };
    setSaving(true);
    try {
      const saved = draft?.id ? await nextApi.automations.update(draft.id, input) : await nextApi.automations.create(input);
      toast.success(draft?.id ? "האוטומציה עודכנה" : "האוטומציה נוצרה והופעלה");
      notifyAutomationsChanged();
      onSaved?.(saved);
      onClose();
    } catch (e) {
      setError((e as { message?: string })?.message || "השמירה נכשלה");
    } finally {
      setSaving(false);
    }
  };

  const updateAction = (i: number, next: AutomationActionDto) => setActions((xs) => xs.map((x, j) => (j === i ? next : x)));

  return (
    <Dialog
      open={open}
      onClose={onClose}
      title={draft?.id ? "עריכת אוטומציה" : "אוטומציה חדשה"}
      icon="sparkle"
      size="lg"
      className="atm-editor"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>
            ביטול
          </Button>
          <Button variant="primary" icon="check" disabled={saving} onClick={() => void save()}>
            {saving ? "שומר…" : draft?.id ? "שמירה" : "יצירה והפעלה"}
          </Button>
        </>
      }
    >
      <div className="atm-form">
        {error ? <Banner tone="danger">{error}</Banner> : null}
        <div className="atm-grid">
          <Field label="שם">
            <TextInput value={name} onChange={(e) => setName(e.target.value)} placeholder="למשל: תזכורת חוב שבועית" data-autofocus />
          </Field>
          <Field label="מוצגת במסך">
            <Select value={screenKey} onChange={setScreenKey} options={(catalog?.screens ?? []).map((s) => ({ value: s.key, label: s.label }))} />
          </Field>
        </div>
        <Field label="תיאור (לא חובה)">
          <TextInput value={description} onChange={(e) => setDescription(e.target.value)} placeholder="למה האוטומציה הזאת קיימת" />
        </Field>

        {/* מתי */}
        <section className="atm-section">
          <h3>
            <Icon name="clock" size={16} />
            מתי
          </h3>
          <OptionChips
            ariaLabel="סוג הפעלה"
            value={trigger.type}
            onChange={(v) => setTrigger(v === "event" ? { type: "event", event: catalog?.events[0]?.key ?? "customer.created" } : DEFAULT_SCHEDULE)}
            options={[
              { value: "schedule", label: "לפי לוח זמנים" },
              { value: "event", label: "כשמשהו קורה" },
            ]}
          />
          {trigger.type === "schedule" ? (
            <>
              <div className="atm-grid">
                <Field label="תדירות">
                  <Select value={trigger.every} onChange={(v) => setSchedule({ every: v as "day" })} options={EVERY} />
                </Field>
                {trigger.every === "week" ? (
                  <Field label="יום">
                    <Select value={String(trigger.weekday ?? 0)} onChange={(v) => setSchedule({ weekday: Number(v) })} options={WEEKDAYS} />
                  </Field>
                ) : null}
                {trigger.every === "month" ? (
                  <Field label="יום בחודש" hint="1–28">
                    <TextInput type="number" min={1} max={28} value={trigger.monthDay ?? 1} onChange={(e) => setSchedule({ monthDay: Math.min(28, Math.max(1, Number(e.target.value) || 1)) })} />
                  </Field>
                ) : null}
                {trigger.every === "once" ? (
                  <Field label="תאריך">
                    <TextInput type="date" value={trigger.date ?? ""} onChange={(e) => setSchedule({ date: e.target.value })} />
                  </Field>
                ) : null}
                <Field label="שעה">
                  <TextInput type="time" className="atm-time" value={trigger.time} onChange={(e) => setSchedule({ time: e.target.value || "08:00" })} />
                </Field>
              </div>
              <Field label="על מה לפעול" hint={catalog?.audiences.find((a) => a.key === trigger.audience)?.hint}>
                <Select
                  value={trigger.audience}
                  onChange={(v) => setSchedule({ audience: v })}
                  options={(catalog?.audiences ?? []).map((a) => ({ value: a.key, label: a.label }))}
                />
              </Field>
              {trigger.audience === "mandates_expiring" ? (
                <Field label="כמה ימים קדימה">
                  <TextInput type="number" min={1} max={365} value={trigger.audienceDays ?? 30} onChange={(e) => setSchedule({ audienceDays: Number(e.target.value) || 30 })} />
                </Field>
              ) : null}
              {trigger.audience !== "none" ? (
                <Checkbox
                  label="פעם אחת לכל רשומה (לא לחזור על אותה פעולה מחר)"
                  checked={trigger.oncePerRecord}
                  onChange={(v) => setSchedule({ oncePerRecord: v })}
                />
              ) : null}
            </>
          ) : (
            <Field label="כש…">
              <Select
                value={trigger.event}
                onChange={(v) => setTrigger({ type: "event", event: v })}
                options={(catalog?.events ?? []).map((e) => ({ value: e.key, label: e.label }))}
              />
            </Field>
          )}
        </section>

        {/* תנאים */}
        {fields.length ? (
          <section className="atm-section">
            <h3>
              <Icon name="filter" size={16} />
              תנאים <span className="atm-muted">(לא חובה — כולם צריכים להתקיים)</span>
            </h3>
            <div className="atm-conds">
              {conditions.map((c, i) => {
                const f = fields.find((x) => x.key === c.field);
                return (
                  <div key={i} className="atm-cond">
                    <Select
                      value={c.field}
                      onChange={(v) => setConditions((xs) => xs.map((x, j) => (j === i ? { ...x, field: v } : x)))}
                      options={fields.map((x) => ({ value: x.key, label: x.label }))}
                    />
                    <Select
                      value={c.op}
                      onChange={(v) => setConditions((xs) => xs.map((x, j) => (j === i ? { ...x, op: v } : x)))}
                      options={Object.entries(catalog?.conditionOps ?? {}).map(([value, label]) => ({ value, label }))}
                    />
                    {NO_VALUE_OPS.has(c.op) ? null : f?.options ? (
                      <Select
                        value={String(c.value ?? "")}
                        onChange={(v) => setConditions((xs) => xs.map((x, j) => (j === i ? { ...x, value: v } : x)))}
                        options={f.options}
                        placeholder="בחירה…"
                      />
                    ) : (
                      <TextInput
                        type={f?.type === "money" || f?.type === "number" ? "number" : "text"}
                        value={c.value ?? ""}
                        onChange={(e) =>
                          setConditions((xs) =>
                            xs.map((x, j) =>
                              j === i ? { ...x, value: f?.type === "money" || f?.type === "number" ? Number(e.target.value) : e.target.value } : x,
                            ),
                          )
                        }
                        placeholder="ערך"
                      />
                    )}
                    <IconButton icon="trash" label="הסרת תנאי" size="sm" onClick={() => setConditions((xs) => xs.filter((_, j) => j !== i))} />
                  </div>
                );
              })}
              <div>
                <Button size="sm" variant="ghost" icon="plus" onClick={() => setConditions((xs) => [...xs, { field: fields[0]?.key ?? "", op: "not_empty" }])}>
                  הוספת תנאי
                </Button>
              </div>
            </div>
          </section>
        ) : null}

        {/* מה לעשות */}
        <section className="atm-section">
          <h3>
            <Icon name="sparkle" size={16} />
            מה לעשות
          </h3>
          {actions.map((a, i) => (
            <div key={i} className="atm-audience">
              <div className="atm-row">
                <Select
                  value={a.type}
                  onChange={(v) => updateAction(i, defaultAction(v as AutomationActionDto["type"]))}
                  options={(catalog?.actions ?? []).map((x) => ({ value: x.key, label: x.label }))}
                  aria-label="סוג פעולה"
                />
                <span className="ck-grow" />
                {actions.length > 1 ? <IconButton icon="trash" label="הסרת פעולה" size="sm" onClick={() => setActions((xs) => xs.filter((_, j) => j !== i))} /> : null}
              </div>
              <ActionFields
                action={a}
                onChange={(next) => updateAction(i, next)}
                recipientModes={recipientModes}
                staff={staff}
                tags={tags}
                reports={catalog?.reports ?? []}
                isTask={entity === "task"}
              />
            </div>
          ))}
          {actions.length < 5 ? (
            <div>
              <Button size="sm" variant="ghost" icon="plus" onClick={() => setActions((xs) => [...xs, defaultAction("create_task")])}>
                הוספת פעולה
              </Button>
            </div>
          ) : null}
        </section>
      </div>
    </Dialog>
  );
}

function RecipientsPicker({
  value,
  onChange,
  modes,
  staff,
}: {
  value: AutomationRecipientsDto;
  onChange: (v: AutomationRecipientsDto) => void;
  modes: Array<{ value: AutomationRecipientsDto["mode"]; label: string }>;
  staff: Array<{ id: string; name: string; hasEmail: boolean }>;
}) {
  const toggle = (id: string) => {
    const ids = value.userIds ?? [];
    onChange({ ...value, userIds: ids.includes(id) ? ids.filter((x) => x !== id) : [...ids, id] });
  };
  return (
    <div className="atm-recipients">
      <OptionChips ariaLabel="למי" value={value.mode} onChange={(m) => onChange({ mode: m as AutomationRecipientsDto["mode"] })} options={modes} />
      {value.mode === "users" ? (
        <div className="atm-chips" role="group" aria-label="אנשי צוות">
          {staff.map((u) => {
            const on = (value.userIds ?? []).includes(u.id);
            return (
              <button key={u.id} type="button" className={`atm-chip ${on ? "is-on" : ""}`} aria-pressed={on} onClick={() => toggle(u.id)}>
                {u.name}
              </button>
            );
          })}
          {!staff.length ? <span className="atm-muted">אין אנשי צוות פעילים</span> : null}
        </div>
      ) : null}
      {value.mode === "emails" ? (
        <Field label="כתובות מייל" hint="מופרדות בפסיק">
          <TextInput
            ltr
            value={(value.emails ?? []).join(", ")}
            onChange={(e) => onChange({ ...value, emails: e.target.value.split(/[,;\s]+/u).filter(Boolean) })}
            placeholder="name@example.com"
          />
        </Field>
      ) : null}
      {value.mode === "record" ? <p className="atm-note">יישלח לכתובת המייל שברשומה. רשומה בלי מייל — מדולגת.</p> : null}
    </div>
  );
}

function TagChips({ tags, onPick }: { tags: CatalogField[]; onPick: (key: string) => void }) {
  if (!tags.length) return null;
  return (
    <div className="atm-ph">
      <span>שדות להכנסה:</span>
      {tags.map((t) => (
        <button key={t.key} type="button" className="atm-chip" onClick={() => onPick(`{{${t.key}}}`)} title={`{{${t.key}}}`}>
          {t.label}
        </button>
      ))}
    </div>
  );
}

function ActionFields({
  action,
  onChange,
  recipientModes,
  staff,
  tags,
  reports,
  isTask,
}: {
  action: AutomationActionDto;
  onChange: (a: AutomationActionDto) => void;
  recipientModes: Array<{ value: AutomationRecipientsDto["mode"]; label: string }>;
  staff: Array<{ id: string; name: string; hasEmail: boolean }>;
  tags: CatalogField[];
  reports: Array<{ key: string; label: string; hint: string }>;
  isTask: boolean;
}) {
  if (action.type === "send_email") {
    return (
      <>
        <RecipientsPicker value={action.to} onChange={(to) => onChange({ ...action, to })} modes={recipientModes} staff={staff} />
        <Field label="נושא">
          <TextInput value={action.subject} onChange={(e) => onChange({ ...action, subject: e.target.value })} />
        </Field>
        <Field label="תוכן">
          <TextArea rows={5} value={action.body} onChange={(e) => onChange({ ...action, body: e.target.value })} />
        </Field>
        <TagChips tags={tags} onPick={(tag) => onChange({ ...action, body: `${action.body}${action.body && !action.body.endsWith(" ") ? " " : ""}${tag}` })} />
      </>
    );
  }
  if (action.type === "create_task") {
    const modes = [
      { value: "unassigned", label: "לא משויכת" },
      { value: "actor", label: "אליי / מי שהפעיל" },
      { value: "user", label: "איש צוות מסוים" },
      ...(isTask ? [{ value: "assignee", label: "האחראי של המשימה" }] : []),
    ];
    return (
      <>
        <Field label="כותרת המשימה">
          <TextInput value={action.title} onChange={(e) => onChange({ ...action, title: e.target.value })} />
        </Field>
        <TagChips tags={tags} onPick={(tag) => onChange({ ...action, title: `${action.title}${action.title ? " " : ""}${tag}` })} />
        <Field label="תיאור (לא חובה)">
          <TextArea rows={3} value={action.description ?? ""} onChange={(e) => onChange({ ...action, description: e.target.value })} />
        </Field>
        <div className="atm-grid">
          <Field label="שיוך">
            <Select value={action.assignTo.mode} onChange={(m) => onChange({ ...action, assignTo: { mode: m as "user" } })} options={modes} />
          </Field>
          {action.assignTo.mode === "user" ? (
            <Field label="איש צוות">
              <Select
                value={action.assignTo.userId ?? ""}
                onChange={(id) => onChange({ ...action, assignTo: { mode: "user", userId: id } })}
                options={staff.map((u) => ({ value: u.id, label: u.name }))}
                placeholder="בחירה…"
              />
            </Field>
          ) : null}
          <Field label="תאריך יעד" hint="ימים מהיום">
            <TextInput type="number" min={0} max={365} value={action.dueInDays} onChange={(e) => onChange({ ...action, dueInDays: Math.max(0, Number(e.target.value) || 0) })} />
          </Field>
          <Field label="עדיפות">
            <Select value={action.priority} onChange={(p) => onChange({ ...action, priority: p as "normal" })} options={PRIORITY} />
          </Field>
        </div>
      </>
    );
  }
  return (
    <>
      <Field label="דוח" hint={reports.find((r) => r.key === action.report)?.hint}>
        <Select value={action.report} onChange={(r) => onChange({ ...action, report: r })} options={reports.map((r) => ({ value: r.key, label: r.label }))} />
      </Field>
      {action.report === "payments_period" ? (
        <Field label="כמה ימים אחורה">
          <TextInput type="number" min={1} max={92} value={action.days ?? 7} onChange={(e) => onChange({ ...action, days: Number(e.target.value) || 7 })} />
        </Field>
      ) : null}
      <RecipientsPicker
        value={action.to}
        onChange={(to) => onChange({ ...action, to })}
        modes={recipientModes.filter((m) => m.value !== "record" && m.value !== "assignee")}
        staff={staff}
      />
    </>
  );
}

export default AutomationEditor;
